import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { getSubscriptionAccess } from '../services/subscriptionService';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [authUser, setAuthUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const mountedRef = useRef(true);

  const hydrate = useCallback(async (user, { silent = false } = {}) => {
    if (!silent && mountedRef.current) setLoading(true);
    if (!user) {
      if (mountedRef.current) {
        setAuthUser(null);
        setProfile(null);
        setTenant(null);
        setSubscription(null);
        setLoading(false);
      }
      return;
    }

    const profileSnap = await getDoc(doc(db, 'users', user.uid));
    const userProfile = profileSnap.exists() ? { id: profileSnap.id, ...profileSnap.data() } : null;
    if (!mountedRef.current) return;

    setAuthUser(user);
    setProfile(userProfile);
    if (!userProfile) {
      setTenant(null);
      setSubscription(null);
      setLoading(false);
      return;
    }

    const isSuper = userProfile.role === 'super_admin' || userProfile.tenantId === 'super_admin';
    if (isSuper) {
      setTenant({ id: 'super_admin', companyName: 'Weilink Signage Platform', status: 'active' });
      setSubscription({ isActive: true, status: 'active', planName: 'Platform', maxScreens: 999, usedScreens: 0, hasScreenSlot: true, message: 'Platform access active.' });
      setLoading(false);
      return;
    }

    const tenantId = userProfile.tenantId;
    const tenantSnap = tenantId ? await getDoc(doc(db, 'tenants', tenantId)) : null;
    const tenantData = tenantSnap?.exists() ? { id: tenantSnap.id, ...tenantSnap.data() } : null;
    const access = tenantId ? await getSubscriptionAccess(tenantId) : null;
    if (!mountedRef.current) return;
    setTenant(tenantData);
    setSubscription(access);
    setLoading(false);
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    const unsubscribe = onAuthStateChanged(auth, user => {
      hydrate(user).catch(error => {
        console.error('Auth hydrate failed', error);
        if (mountedRef.current) setLoading(false);
      });
    });
    return () => {
      mountedRef.current = false;
      unsubscribe();
    };
  }, [hydrate]);

  // Keep tenant entitlement current while the console stays open. This means a
  // Super Admin renewal/suspension is reflected without a fresh login.
  useEffect(() => {
    if (!authUser || !profile || profile.role === 'super_admin' || profile.tenantId === 'super_admin') return undefined;
    const refreshEntitlement = () => hydrate(authUser, { silent: true }).catch(error => console.warn('Entitlement refresh failed', error));
    const timer = window.setInterval(refreshEntitlement, 60_000);
    const onFocus = () => refreshEntitlement();
    window.addEventListener('focus', onFocus);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [authUser, profile, hydrate]);

  const refresh = useCallback(() => auth.currentUser ? hydrate(auth.currentUser, { silent: true }) : Promise.resolve(), [hydrate]);

  const value = useMemo(() => ({
    authUser,
    profile,
    tenant,
    subscription,
    loading,
    isSuperAdmin: profile?.role === 'super_admin' || profile?.tenantId === 'super_admin',
    refresh,
    logout: () => signOut(auth)
  }), [authUser, profile, tenant, subscription, loading, refresh]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const value = useContext(AuthContext);
  if (!value) throw new Error('useAuth must be used inside AuthProvider');
  return value;
}
